/**
 * Generic W3C Server-Sent Events frame parser.
 *
 * SYNC: ported from theplaidoyer-frontend/src/adapters/sse/parser.ts
 *       @ FE SHA 65f3ebfd62430a199b586e5304c01810563514fd (2026-05-30).
 *       Status: forked — DE-ZOD'd. The add-in has NO zod dependency
 *       (CLAUDE.md §4 / gate-check), so instead of validating the
 *       payload against a `z.ZodType` this version takes a plain
 *       validator callback `(raw: unknown) => T | null`. Returning
 *       `null` skips the frame (drift / unknown event); the validator
 *       owns its own error policy. Everything else — the UTF-8
 *       boundary-split handling, multi-`data:` line join, comment-line
 *       skip, `\r\n`/`\n`/`\r` terminators, and AbortSignal teardown —
 *       is preserved verbatim.
 *
 * The W3C SSE spec accepts `\n`, `\r\n`, and `\r` as line terminators and
 * defines an event boundary as two consecutive end-of-line markers. Lines
 * starting with `:` are comments and MUST be ignored. Multiple `data:`
 * lines in a single event are joined with `\n`. We honour all of that.
 */

/** A single typed SSE event yielded by {@link createSSEStream}. */
export interface SSEEvent<T> {
  /** The `event:` field. Defaults to `'message'` per the W3C spec. */
  event: string
  /** The validated payload (post JSON parse + validator check). */
  data: T
  /** The `id:` field if present. */
  id?: string
}

/** Error thrown when an SSE frame cannot be parsed at the framing level
 *  (missing body, missing data payload). Validator-level rejections do
 *  NOT throw — the frame is skipped. */
export class SSEParseError extends Error {
  constructor(
    message: string,
    public readonly cause?: unknown,
  ) {
    super(message)
    this.name = 'SSEParseError'
  }
}

/**
 * Validator callback applied to each frame's parsed payload. Returns the
 * typed event on success, or `null` to skip the frame (schema drift,
 * unknown event type, defensively-dropped key). It MUST NOT throw for a
 * recoverable mismatch — the stream keeps flowing.
 */
export type SSEValidator<T> = (raw: unknown) => T | null

/** Options accepted by {@link createSSEStream}. */
export interface CreateSSEStreamOptions {
  /** Abort the upstream reader when this signal fires. */
  signal?: AbortSignal
}

/** Result of scanning a buffer for a complete event block. */
interface FrameScan {
  /** The complete frame content (without the trailing boundary). Null if no frame yet. */
  frame: string | null
  /** Remainder of the buffer after the boundary. */
  rest: string
}

/**
 * Locate the next event boundary — `\r\n\r\n`, `\n\n`, or `\r\r` — and
 * return the frame ahead of it plus the remainder behind.
 *
 * Returning `null` means no complete frame is in the buffer yet and the
 * caller should keep accumulating.
 */
function extractFrame(buffer: string): FrameScan {
  // Check all three SSE event boundaries. We pick the earliest match so a
  // `\n\n` buried before a later `\r\n\r\n` still yields the first frame.
  let earliest = -1
  let boundaryLen = 0
  for (const [boundary, len] of [
    ['\r\n\r\n', 4] as const,
    ['\n\n', 2] as const,
    ['\r\r', 2] as const,
  ]) {
    const idx = buffer.indexOf(boundary)
    if (idx !== -1 && (earliest === -1 || idx < earliest)) {
      earliest = idx
      boundaryLen = len
    }
  }
  if (earliest === -1) return { frame: null, rest: buffer }
  return {
    frame: buffer.slice(0, earliest),
    rest: buffer.slice(earliest + boundaryLen),
  }
}

/** Parsed-but-unvalidated SSE frame. */
interface RawSSEFrame {
  event: string
  id?: string
  data: string | null
}

/**
 * Parse one frame block (the text between event boundaries) into its
 * `event` / `id` / `data` components per the W3C spec. Comment lines
 * (starting with `:`) and unknown fields are silently ignored. Multiple
 * `data:` lines are joined with `\n`.
 */
function parseFrame(block: string): RawSSEFrame | null {
  if (!block) return null
  // Per spec, lines inside a block are separated by \n, \r\n, or \r. Split
  // on any of them — order matters: \r\n before \r so the pair isn't
  // counted twice.
  const lines = block.split(/\r\n|\n|\r/)
  let event = 'message'
  let id: string | undefined
  const dataLines: string[] = []

  for (const line of lines) {
    if (line === '') continue
    if (line.startsWith(':')) continue // comment
    const colonAt = line.indexOf(':')
    // No colon → the whole line is a field name with empty value. The spec
    // allows this — we just have nothing useful to do with it here.
    if (colonAt === -1) continue
    const field = line.slice(0, colonAt)
    // Per spec, a single leading space in the value is stripped.
    let value = line.slice(colonAt + 1)
    if (value.startsWith(' ')) value = value.slice(1)

    switch (field) {
      case 'event':
        if (value !== '') event = value
        break
      case 'id':
        id = value
        break
      case 'data':
        dataLines.push(value)
        break
      // `retry` and any unknown field: ignore.
      default:
        break
    }
  }

  if (dataLines.length === 0) {
    // Spec-compliant "field-only" frame — no data to yield.
    return { event, id, data: null }
  }
  return { event, id, data: dataLines.join('\n') }
}

/**
 * Consume a fetch `Response` whose body is a `text/event-stream` and yield
 * typed events one at a time.
 *
 * Each event's `data:` payload is parsed as JSON (falling back to the raw
 * string on parse failure so primitives / non-JSON payloads still flow),
 * then handed to `validate`. Malformed framing always throws
 * {@link SSEParseError}; validator rejections (`null`) skip the frame so
 * the stream keeps flowing.
 *
 * The returned iterable cancels the upstream reader when `options.signal`
 * fires, so callers can hand this directly to a UI `AbortController`.
 */
export async function* createSSEStream<T>(
  response: Response,
  validate: SSEValidator<T>,
  options?: CreateSSEStreamOptions,
): AsyncIterable<SSEEvent<T>> {
  const body = response.body
  if (!body) {
    throw new SSEParseError('Response has no body')
  }

  const reader = body.getReader()
  const decoder = new TextDecoder('utf-8')
  const signal = options?.signal

  // If the signal is aborted mid-stream we cancel the reader so the socket
  // is released promptly rather than waiting for the server to time out.
  const abortHandler = () => {
    // Swallow any error from cancel — the iterator already exited.
    void reader.cancel().catch(() => {
      // best-effort teardown — iterator already exited, failure is non-fatal
    })
  }
  if (signal) {
    if (signal.aborted) abortHandler()
    else signal.addEventListener('abort', abortHandler, { once: true })
  }

  let buffer = ''

  try {
    for (;;) {
      if (signal?.aborted) break

      const { done, value } = await reader.read()
      if (done) break

      // `stream: true` keeps incomplete multi-byte sequences parked until
      // the next chunk arrives — this is the load-bearing bit for UTF-8
      // boundary splits.
      buffer += decoder.decode(value, { stream: true })

      // Drain every complete frame currently in the buffer.
      for (;;) {
        const { frame, rest } = extractFrame(buffer)
        if (frame === null) break
        buffer = rest
        const parsed = parseFrame(frame)
        if (!parsed || parsed.data === null) continue
        const ev = validateAndBuild(parsed, validate)
        if (ev) yield ev
      }
    }

    // Flush the decoder — final `stream: false` call emits any stray bytes
    // still sitting in the decoder's internal buffer.
    buffer += decoder.decode()

    // A trailing frame without a double-newline is still a legitimate
    // final event — treat it as one.
    if (buffer.trim().length > 0) {
      const parsed = parseFrame(buffer)
      if (parsed && parsed.data !== null) {
        const ev = validateAndBuild(parsed, validate)
        if (ev) yield ev
      }
    }
  } finally {
    if (signal) signal.removeEventListener('abort', abortHandler)
    try {
      reader.releaseLock()
    } catch {
      // Reader already cancelled / released — nothing to do.
    }
  }
}

/**
 * Parse a frame's JSON payload, hand it to `validate`, and wrap into an
 * {@link SSEEvent}. A `null` from the validator means "skip" (drift /
 * unknown / defensively-dropped) — the iterator simply doesn't yield it,
 * the stream keeps flowing.
 */
function validateAndBuild<T>(
  frame: RawSSEFrame,
  validate: SSEValidator<T>,
): SSEEvent<T> | null {
  if (frame.data === null) {
    throw new SSEParseError('SSE frame missing data payload')
  }

  // Try JSON first, fall back to the raw string — some backends stream
  // plain primitives on the `data:` line (token chunks).
  let payload: unknown
  try {
    payload = JSON.parse(frame.data)
  } catch {
    payload = frame.data
  }

  const data = validate(payload)
  if (data === null) return null
  return { event: frame.event, id: frame.id, data }
}
